import * as THREE from "three";
import AddExtrudedLoopAnnotationCmd from "./AddExtrudedLoopAnnotationCmd";
import CommandIdEnum from "../enums/CommandIdEnums";
import ICanvasInputListener from "../logic/ICanvasInputListener";
import Document from "../model/document/Document";
import CommandStack from "../logic/CommandStack";
import Placement3d from "../model/Placement3d";
import CircleAnnotationData from "../model/sceneObjectData/CircleAnnotationData";
import CircleHintHelper from "./commandHelpers/CircleHintHelper";
import { SceneManager } from "../model/SceneManager";
import EditHandleTypeEnum from "../enums/EditHandleTypeEnum";

export default class AddCircleAnnotationCmd extends AddExtrudedLoopAnnotationCmd<CircleAnnotationData> {

    #centerHint = new CircleHintHelper();

    constructor(commandStack: CommandStack, canvasInputListener: ICanvasInputListener, document: Document) {
        super(CommandIdEnum.AddCircleAnnotation, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.addCircleAnnotation.title';
        this.initialPrompResourceKey = 'command.addCircleAnnotation.selectFirstPoint';
        this.nextPointPromptResourceKey = 'command.addCircleAnnotation.selectSecondPoint';
        this.#centerHint.addToScene(SceneManager.scene);
    }

    protected createInitialData(placement: Placement3d, depth: number): CircleAnnotationData {
        return new CircleAnnotationData(placement, 0, depth);
    }

    protected onSubsequentPoint(data: CircleAnnotationData, alignedPoint: THREE.Vector3): 'continue' | 'showFinish' | 'autoFinish' {
        data.radius = data.placement.location.distanceTo(alignedPoint);
        data.applyAreaBasedFontSize();
        data.applyAreaBasedShowAreaText();
        return 'autoFinish';
    }

    protected buildPreviewData(data: CircleAnnotationData, alignedPoint: THREE.Vector3): CircleAnnotationData {
        const preview = data.clone();
        preview.radius = data.placement.location.distanceTo(alignedPoint);
        preview.applyAreaBasedFontSize();
        return preview;
    }

    protected creationMeasurementHandleType(): EditHandleTypeEnum {
        return EditHandleTypeEnum.CIRCLE_RADIUS;
    }

    protected getRefAxisPoints(data: CircleAnnotationData): THREE.Vector3[] {
        return [data.placement.location.clone()];
    }

    // Rejects a zero-radius double-click on the center: the command stays open (this.data is
    // already set), so the very next click simply retries setting the radius.
    protected isReadyToFinish(data: CircleAnnotationData): boolean {
        return data.radius > 1e-6;
    }

    protected finishButtonResourceKey(): string {
        return 'command.addCircleAnnotation.finish';
    }

    protected updatePointHint(point: THREE.Vector3, normal: THREE.Vector3): void {
        this.#centerHint.update(point, normal);
        this.#centerHint.show();
    }

    protected hidePointHint(): void {
        this.#centerHint.hide();
    }

    dispose(): void {
        this.#centerHint.dispose();
        super.dispose();
    }
}
